import type { ReelItem as KiboReelItem } from "@/components/kibo-ui/reel"
import {
  Reel as KReel,
  ReelContent,
  ReelControls,
  ReelImage,
  ReelItem as KReelItem,
  ReelMuteButton,
  ReelNavigation,
  ReelNextButton,
  ReelPlayButton,
  ReelPreviousButton,
  ReelSegmentedProgress,
  ReelVideo,
} from "@/components/kibo-ui/reel"

export interface AnnouncementData {
  id: number | string
  content: string
  created_at: string
  author?: { id: number; name: string; verified?: boolean }
  cover_url?: string | null
  video_url?: string | null
  images?: { id: number | string; url: string }[]
  videos?: { id: number | string; url: string }[]
}

interface Props {
  item: AnnouncementData
  mode?: "default" | "carousel"
}

export default function AnnouncementFeedItem({
  item,
  mode = "default",
}: Props) {
  const videoItems =
    item.videos && item.videos.length > 0
      ? item.videos.map((v) => ({ src: v.url, type: "video" as const }))
      : item.video_url
        ? [{ src: String(item.video_url), type: "video" as const }]
        : []
  const hasVideo = videoItems.length > 0
  const imageItems =
    (item.images?.length ?? 0) > 0
      ? (item.images ?? []).map((img) => ({
          src: img.url,
          type: "image" as const,
        }))
      : item.cover_url
        ? [{ src: String(item.cover_url), type: "image" as const }]
        : []
  const hasImages = imageItems.length > 0
  const videoReels: KiboReelItem[] = videoItems.map((m, idx) => ({
    id: `v-${idx}`,
    type: "video",
    src: m.src,
  }))
  const imageReels: KiboReelItem[] = imageItems.map((m, idx) => ({
    id: `i-${idx}`,
    type: "image",
    src: m.src,
    duration: 4,
  }))
  const reels: KiboReelItem[] = [...videoReels, ...imageReels]

  const containerClasses =
    mode === "carousel" ? "space-y-0 w-[200px] sm:w-[240px]" : "space-y-0 mb-4"

  return (
    <div className={containerClasses}>
      {mode !== "carousel" && (
        <div
          className="prose prose-sm dark:prose-invert mb-2 max-w-none"
          dangerouslySetInnerHTML={{ __html: item.content }}
        />
      )}
      {hasVideo || hasImages ? (
        <div className="relative overflow-hidden rounded-xl">
          <KReel
            data={reels}
            className={
              mode === "carousel"
                ? "aspect-[9/16] w-full bg-black"
                : "aspect-[9/16] max-h-[50vh] w-full bg-black md:max-h-[60vh] lg:max-h-[70vh]"
            }
          >
            <ReelSegmentedProgress />
            <ReelContent>
              {(reel) => (
                <KReelItem key={reel.id}>
                  <div className="h-full w-full">
                    {reel.type === "video" ? (
                      <ReelVideo src={reel.src} />
                    ) : (
                      <ReelImage src={reel.src} />
                    )}
                  </div>
                </KReelItem>
              )}
            </ReelContent>
            <ReelNavigation />
            <ReelControls>
              <ReelPreviousButton />
              <div className="flex gap-2">
                <ReelPlayButton />
                <ReelMuteButton />
              </div>
              <ReelNextButton />
            </ReelControls>
          </KReel>
        </div>
      ) : null}
    </div>
  )
}
